import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';

import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { SelectOption } from 'src/app/components/form/select-field/select-field.config';
import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { Regulator, VLT_STATUS_LABELS, Vlt, VltPayload, VltStatus } from 'src/app/models/entities/regulator';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { RegulatorsService } from 'src/app/services/api/regulators.service';
import { FormService } from 'src/app/services/form.service';
import { MainLayoutService } from 'src/app/services/main-layout.service';
import { RouteService } from 'src/app/services/route.service';
import { RegulatorContextBoxComponent, RegulatorContextItem } from '../components/regulator-context-box/regulator-context-box.component';
import { apiErrorMessage, dateTimeLocalToIso, isValidationError, isoToDateTimeLocal } from '../regulators-ui.utils';

type VltTextField = 'code' | 'serialNumber' | 'manufacturer' | 'model' | 'venue' | 'address' | 'city' | 'country'
  | 'latitude' | 'longitude' | 'status' | 'lastSeenTime';

@Component({
  selector: 'app-regulator-vlt-edit-page',
  imports: [ReactiveFormsModule, InputFieldComponent, SelectFieldComponent, LoaderComponent, RegulatorContextBoxComponent],
  templateUrl: './regulator-vlt-edit-page.component.html',
  styleUrl: './regulator-vlt-edit-page.component.scss',
})
export class RegulatorVltEditPageComponent implements OnInit, OnDestroy {

  readonly regulatorId: number;
  readonly vltId: number | null;

  readonly statusOptions: SelectOption[] = Object.entries(VLT_STATUS_LABELS).map(([key, value]) => ({ key, value }));

  form = new FormGroup({
    code: new FormControl<string>('', { nonNullable: true, validators: Validators.required }),
    serialNumber: new FormControl<string>('', { nonNullable: true, validators: Validators.required }),
    manufacturer: new FormControl<string>('', { nonNullable: true }),
    model: new FormControl<string>('', { nonNullable: true }),
    venue: new FormControl<string>('', { nonNullable: true, validators: Validators.required }),
    address: new FormControl<string>('', { nonNullable: true }),
    city: new FormControl<string>('', { nonNullable: true, validators: Validators.required }),
    country: new FormControl<string>('', { nonNullable: true }),
    latitude: new FormControl<string>('', { nonNullable: true, validators: [Validators.min(-90), Validators.max(90)] }),
    longitude: new FormControl<string>('', { nonNullable: true, validators: [Validators.min(-180), Validators.max(180)] }),
    status: new FormControl<string>('ONLINE', { nonNullable: true, validators: Validators.required }),
    lastSeenTime: new FormControl<string>('', { nonNullable: true }),
  });

  regulator: Regulator | null = null;
  vlt: Vlt | null = null;
  contextItems: RegulatorContextItem[] = [];
  isLoading = false;
  isSaving = false;

  constructor(
    private regulatorsService: RegulatorsService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private alertNotificationService: AlertNotificationService,
    private formService: FormService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.regulatorId = Number(this.route.snapshot.paramMap.get('regulatorId'));
    this.vltId = Number(this.route.snapshot.paramMap.get('vltId')) || null;
  }

  get isUpdate(): boolean {
    return this.vltId !== null;
  }

  ngOnInit(): void {
    const vlt$: Observable<any> = this.vltId ? this.regulatorsService.detailVlt(this.regulatorId, this.vltId) : of(null);

    this.isLoading = true;
    forkJoin({ regulator: this.regulatorsService.detail(this.regulatorId), vlt: vlt$ }).subscribe({
      next: ({ regulator, vlt }: any) => {
        this.isLoading = false;
        this.applyContext(regulator?.data?.regulator, vlt?.data?.vlt ?? null);
      },
      error: () => {
        this.isLoading = false;
        this.router.navigate(['/error/404']);
      }
    });
  }

  ngOnDestroy(): void {
    this.mainLayoutService.setBreadcrumbs(null);
  }

  control(name: VltTextField): FormControl {
    return this.form.get(name) as FormControl;
  }

  goBack(): void {
    if (this.vltId) {
      this.routeService.regulatorVltDashboard(this.regulatorId, this.vltId);
    } else {
      this.routeService.regulatorVlts(this.regulatorId);
    }
  }

  submitForm(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.isSaving) {
      return;
    }

    const payload = this.buildPayload();
    const request$ = this.vltId
      ? this.regulatorsService.updateVlt(this.regulatorId, this.vltId, payload)
      : this.regulatorsService.createVlt(this.regulatorId, payload);

    this.formService.resetErrors(this.form);
    this.isSaving = true;
    request$.subscribe({
      next: (response: any) => {
        this.isSaving = false;
        this.alertNotificationService.success('VLT saved successfully');
        const savedId = response?.data?.vlt?.vltId ?? this.vltId;
        if (savedId) {
          this.routeService.regulatorVltDashboard(this.regulatorId, savedId);
        } else {
          this.routeService.regulatorVlts(this.regulatorId);
        }
      },
      error: (error: any) => {
        this.isSaving = false;
        if (isValidationError(error)) {
          this.formService.setValidationErrors(this.form, error.error.error.validationErrors);
        }
        this.alertNotificationService.error(apiErrorMessage(error, 'Error saving VLT'));
      }
    });
  }

  private buildPayload(): VltPayload {
    const value = this.form.getRawValue();
    const text = (v: string) => v.trim() || null;
    const num = (v: string | number) => (v === '' || v === null || v === undefined) ? null : Number(v);

    return {
      code: value.code.trim(),
      serialNumber: value.serialNumber.trim(),
      manufacturer: text(value.manufacturer),
      model: text(value.model),
      venue: value.venue.trim(),
      address: text(value.address),
      city: value.city.trim(),
      country: text(value.country),
      latitude: num(value.latitude),
      longitude: num(value.longitude),
      status: value.status as VltStatus,
      lastSeenTime: dateTimeLocalToIso(value.lastSeenTime),
    };
  }

  private applyContext(regulator: Regulator | null | undefined, vlt: Vlt | null): void {
    if (!regulator) {
      return;
    }

    this.regulator = regulator;
    this.vlt = vlt;

    if (vlt) {
      this.form.patchValue({
        code: vlt.code ?? '',
        serialNumber: vlt.serialNumber ?? '',
        manufacturer: vlt.manufacturer ?? '',
        model: vlt.model ?? '',
        venue: vlt.venue ?? '',
        address: vlt.address ?? '',
        city: vlt.city ?? '',
        country: vlt.country ?? '',
        latitude: vlt.latitude !== null && vlt.latitude !== undefined ? String(vlt.latitude) : '',
        longitude: vlt.longitude !== null && vlt.longitude !== undefined ? String(vlt.longitude) : '',
        status: vlt.status ?? 'ONLINE',
        lastSeenTime: isoToDateTimeLocal(vlt.lastSeenTime),
      });
    }

    this.contextItems = vlt
      ? [{ label: 'Regulator', code: regulator.code, value: regulator.name }, { label: 'VLT', code: vlt.code }]
      : [{ label: 'Regulator', code: regulator.code, value: regulator.name }];

    const vltListLink = `/regulators/${regulator.regulatorId}/vlt`;
    this.mainLayoutService.setBreadcrumbs(vlt
      ? [
          { label: 'Regulators', routerLink: '/regulators' },
          { label: regulator.code },
          { label: 'VLT', routerLink: vltListLink },
          { label: vlt.code, routerLink: `${vltListLink}/${vlt.vltId}` },
          { label: 'Edit' },
        ]
      : [
          { label: 'Regulators', routerLink: '/regulators' },
          { label: regulator.code },
          { label: 'VLT', routerLink: vltListLink },
          { label: 'Create' },
        ]);
  }
}
